import { useEffect, useRef, useState } from "react";
import { useWindows } from "@/os/window/context";
import { ACHIEVEMENTS, EGGS, useAchievements } from "@/os/state/achievements";
import { useSettings } from "@/os/state/settings";
import { useTheme } from "@/os/state/theme";
import { useToast } from "@/os/state/toast";
import { play } from "@/lib/sound";

interface Line {
    id: number;
    text: string;
    tone: "out" | "in" | "ok";
}

const HELP = [
    "available commands:",
    "  help            this menu",
    "  who am i        identity card",
    "  neofetch        the system card, but make it krithi",
    "  cat <file>      read a file from my_brain",
    "  projects        things i've built",
    "  contact         email, github, linkedin",
    "  certs           certifications, honestly listed",
    "  skills          the stack, grouped",
    "  ls brain/       peek inside my_brain",
    "  achievements    check your progress",
    "  pwd             where are you, exactly",
    "  coffee          brew one (does nothing. everything.)",
    "  sudo sleep      try it",
    "  sudo hire       worth a shot",
    "  resume          open resume_final_final_v7.pdf",
    "  open <app>      launch any desktop app",
    "  clear           wipe the scrollback",
    "  exit            close the terminal",
];

const NEOFETCH = (
    eggs: number,
    s: { curiosity: number; overthinking: number; sleep: number; tolerance: number },
    modes: { chaotic: boolean; bedtime: boolean; impatient: boolean },
): string[] => {
    const activeModes = [
        modes.chaotic && "chaotic research",
        modes.bedtime && "bedtime conflict",
        modes.impatient && "performance impatience",
    ].filter(Boolean);
    return [
        "  ██╗  ██╗       krithi@krithi-os",
        "  ██║ ██╔╝       ──────────────────────────────",
        "  █████╔╝        OS: KRITHI.OS v7.0 (warm cream, light theme)",
        "  ██╔═██╗        Host: krithi's brain",
        "  ██║  ██╗       Uptime: coding since 2021 · EG India era since Apr 2025",
        "  ╚═╝  ╚═╝       Shell: krithi-sh (safe, pretend)",
        "                 Terminal: this one",
        "                 Stack: python · fastapi · .net 10 · next.js · angular · aws bedrock · pgvector",
        `                 CPU: curiosity ${s.curiosity}/100 ${s.curiosity >= 70 ? "(overclocked)" : ""}`,
        `                 GPU: overthinking ${s.overthinking}/100 (integrated)`,
        `                 Sleep daemon: ${s.sleep}/100 ${s.sleep >= 70 ? "(yawning)" : "(not found)"}`,
        `                 Patience for slow apps: ${s.tolerance}/100 ${s.tolerance <= 30 ? "(loading screens armed)" : ""}`,
        "                 Tabs: 37 open, 0 closable",
        "                 Memory: 8 projects, 1 saxophone, 8 childhood side quests",
        `                 Eggs found: ${eggs}/${EGGS.length}`,
        ...(activeModes.length ? [`                 Modes: ${activeModes.join(" + ")} ON`] : []),
        "                 Coffee: always brewing",
    ];
};

const CAT_FILES: Record<string, string[]> = {
    "things_i_overthink.md": [
        "- whether the solution i just wrote is the best solution, or just the first one that worked.",
        "- whether i should refactor, or leave it alone, or refactor the refactor.",
        "- whether the tab count is a personality trait or a warning label. (verdict: both.)",
    ],
    "things_that_make_me_me.txt": [
        "- the saxophone. an instrument that flirts with jazz even in the practice room.",
        "- food, and watching mukbang videos. i enjoy eating far more than i enjoy cooking.",
        "- toastmasters. turning 'public speaking' from a boss fight into a trainable skill.",
    ],
    "current_side_quest.txt": ["status: between side quests. the tabs always know first."],
    "origin_story.md": [
        "every OS boots from somewhere. this file is reserved for the real story — still being written. no fictional memories allowed.",
    ],
    "37_tabs_open.txt": ["37 tabs. some are load-bearing. open the 37_tabs_open icon for the full simulated browser."],
    "resume.txt": ["nice try. use: resume — or click resume_final_final_v7.pdf like a professional."],
};

const APP_ALIASES: Record<string, string> = {
    my_brain: "my_brain",
    brain: "my_brain",
    "my brain": "my_brain",
    experiments: "experiments",
    bugs: "bugs_i_fixed",
    bugs_i_fixed: "bugs_i_fixed",
    outside: "outside_the_terminal",
    outside_the_terminal: "outside_the_terminal",
    thoughts: "unnecessary_thoughts",
    unnecessary_thoughts: "unnecessary_thoughts",
    future: "future_exe",
    "future.exe": "future_exe",
    future_exe: "future_exe",
    resume: "resume_pdf",
    recycle: "recycle_bin",
    "recycle bin": "recycle_bin",
    recycle_bin: "recycle_bin",
    terminal: "terminal",
    tabs: "tabs",
    "37_tabs_open": "tabs",
    personality: "personality",
    achievements: "achievements",
};

const QUICK = ["help", "who am i", "neofetch", "projects", "contact", "coffee", "resume"];

export function TerminalApp() {
    const [lines, setLines] = useState<Line[]>([
        { id: 0, text: "krithi.os terminal v2.4 — type 'help'. nothing here can break; it's all pretend.", tone: "ok" },
    ]);
    const [value, setValue] = useState("");
    const [history, setHistory] = useState<string[]>([]);
    const hIdx = useRef(-1);
    const idRef = useRef(0);
    const scrollRef = useRef<HTMLDivElement>(null);
    const { open, close } = useWindows();
    const { trackTerminal, trackEgg, unlocked, unlockedCount, eggs, allEggs } = useAchievements();
    const { sliders, modes } = useSettings();
    const { setTheme } = useTheme();
    const { push } = useToast();

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
    }, [lines]);

    const out = (texts: string[]) =>
        setLines((prev) => [...prev, ...texts.map((t) => ({ id: ++idRef.current, text: t, tone: "out" as const }))]);
    const valid = () => {
        play("click");
        trackTerminal();
    };

    const run = (raw: string) => {
        const cmd = raw.trim();
        if (!cmd) return;
        setLines((prev) => [...prev, { id: ++idRef.current, text: `krithi@krithi-os:~$ ${cmd}`, tone: "in" }]);
        setHistory((h) => [cmd, ...h].slice(0, 30));
        hIdx.current = -1;
        let c = cmd.toLowerCase();
        const aliasNote: string[] = [];

        if (c.startsWith("is ")) {
            aliasNote.push("parsing 'is' as 'ls' — this terminal is forgiving.");
            c = "ls" + c.slice(2);
        }
        if (c.startsWith("ls ")) {
            c = "ls " + c.slice(3).replace(/^~\//, "").replace(/\/$/, "");
        }

        if (c === "help") {
            out(HELP);
            valid();
            return;
        }
        if (c === "who am i" || c === "whoami") {
            out([
                "krithi u k — applied ai engineer / software developer at EG India, mangaluru.",
                "b.e. computer science, nmamit (8.93/10). research intern at NITK before this.",
                "vision-model document pipelines by day, saxophone by night, 37 tabs at all times.",
                "status: currently reading this terminal output. hi.",
            ]);
            valid();
            return;
        }
        if (c === "projects") {
            out([
                "1. ai_price_list_extraction — danish price lists → structured data via claude vision on bedrock · EG India, internal",
                "2. construction_cost_estimation — 6 product areas, angular 21 + .net 10, ai tender import · EG India, internal",
                "3. product_alternative_discovery — jaccard similarity over a very skewed catalogue · EG India, internal",
                "4. rag_chatbot — grounded answers with streamed citations (langgraph, chromadb) · github.com/Kay131103/rag-chatbot",
                "5. realtime_voice_agent — gemini live + websockets, barge-in · github.com/Kay131103/realtime-voiceagent",
                "6. ai_code_review_agent — inline PR reviews, rules + llm pass · github.com/Kay131103/pr-review-agent",
                "7. video_captioning + knowledge_graph_search — NITK research",
                "tip: open experiments — or click the icon like a normal person.",
            ]);
            valid();
            return;
        }
        if (c === "contact" || c === "contact me" || c === "hire me") {
            out([
                "krithi u k — applied ai engineer / software developer, EG India.",
                "  email      krithidps2003@gmail.com",
                "  phone      +91 94496 89659",
                "  github     https://github.com/Kay131103",
                "  linkedin   https://www.linkedin.com/in/krithi-u-k-6076b1163/",
                "  based in   mangaluru, karnataka, india",
                "response time: faster than the legacy app used to be.",
            ]);
            valid();
            return;
        }
        if (c === "certs" || c === "certifications") {
            out([
                "google ai essentials · google prompting essentials · microsoft azure ai essentials",
                "generative ai: introduction to large language models",
                "artificial intelligence foundations: neural networks",
                "postgresql essential training · introduction to deep learning",
                "note: the certificates are real. the learning mostly happened in the pull requests.",
            ]);
            valid();
            return;
        }
        if (c === "skills") {
            out([
                "languages: python, c#, javascript, typescript, sql, java, c/c++",
                "ai & data: llms, vision-language models, prompt engineering, rag, embeddings, vector search, document ai, knowledge graphs",
                "backend & frontend: fastapi, asp.net core / .net 10, rest apis, next.js, react, angular, tailwind",
                "cloud & engineering: aws bedrock, ecs fargate, s3, ec2, amplify, postgresql, pgvector, docker, github actions, cloudformation, git",
                "soft: debugging by ear (saxophone transferable skills)",
            ]);
            valid();
            return;
        }
        if (c === "neofetch" || c === "krithifetch") {
            out(NEOFETCH(eggs.length, sliders, modes));
            valid();
            return;
        }
        if (c === "cat" || c.startsWith("cat ")) {
            const name = c === "cat" ? "" : c.slice(4).trim().replace(/^(brain|my_brain)\//, "");
            valid();
            if (!name) {
                out(["cat: which file? try: cat things_i_overthink.md"]);
                return;
            }
            const content = CAT_FILES[name];
            if (content) {
                out(content);
                return;
            }
            if (name.toLowerCase() === "youfoundeverything.txt") {
                if (allEggs) {
                    out([
                        "If you have made it this far, you're either exceptionally curious or very committed to avoiding your inbox. Either way, I think we'll get along. Thanks for exploring my little corner of the internet.",
                        "",
                        "— krithi",
                    ]);
                } else {
                    out([
                        "cat: permission denied.",
                        `this file only opens for those who found every easter egg. (${eggs.length}/${EGGS.length} so far.)`,
                    ]);
                }
                return;
            }
            out([`cat: ${name}: no such file. try 'ls brain/' to see what's here.`]);
            return;
        }
        if (c === "theme" || c.startsWith("theme ")) {
            const name = c.slice(5).trim();
            valid();
            if (!name) {
                out(["usage: theme <butter | lavender | cream>", "a hidden knob. you didn't see this."]);
                return;
            }
            if (name === "butter" || name === "lavender" || name === "cream") {
                setTheme(name);
                out([`theme twisted: ${name}. the wallpaper submits.`]);
                push("system", `theme twisted — ${name}`, "a hidden terminal command did this. tell no one.");
                return;
            }
            out([`theme: unknown shade '${name}'. the closet holds: butter, lavender, cream.`]);
            return;
        }
        if (c === "achievements") {
            const got = ACHIEVEMENTS.filter((a) => unlocked[a.id]).map((a) => `  ✓ ${a.title}`);
            out([
                `progress: ${unlockedCount}/${ACHIEVEMENTS.length} achievements · ${eggs.length}/${EGGS.length} easter eggs`,
                ...(got.length ? got : ["  none yet. the OS believes in you. statistically."]),
                "opening the full tracker ...",
            ]);
            open("achievements");
            valid();
            return;
        }
        if (c === "pwd") {
            out(["/home/krithi/brain — it's roomy in here."]);
            valid();
            return;
        }
        if (c === "sudo hire") {
            out([
                "checking sudoers file ... you are not in it. yet.",
                "flattery received and logged. the OS is considering a forwarding recommendation.",
            ]);
            valid();
            return;
        }
        if (c.startsWith("ls")) {
            const target = c.slice(2).trim();
            valid();
            if (!target || target === ".") {
                out([
                    "my_brain/   bugs_i_fixed/   experiments/   outside_the_terminal/",
                    "unnecessary_thoughts/   future.exe   resume_final_final_v7.pdf",
                    "recycle bin/   terminal.exe   37_tabs_open/   personality_settings.exe",
                ]);
                return;
            }
            if (["brain", "brain/", "my_brain", "my_brain/"].includes(target)) {
                out([
                    "37_tabs_open.txt   things_i_overthink.md   current_side_quest.txt",
                    "origin_story.md   things_that_make_me_me.txt",
                    "more_questions.txt (unlocks at curiosity >= 70)",
                ]);
                return;
            }
            out([`ls: cannot access '${target}': no such directory. calm — nothing broke.`]);
            return;
        }
        if (c === "coffee") {
            out(["brewing ...", "done. 0% coffee, 100% tabs.", "(this counts as a valid command and a lifestyle.)"]);
            trackEgg("egg_terminal_coffee");
            valid();
            return;
        }
        if (c === "sudo sleep" || c === "sudo sleep()") {
            out([
                "permission denied: sleep cannot be sudo'd. it must be earned.",
                sliders.sleep >= 70
                    ? "your sleep optimization slider is high though. the OS respects the intention."
                    : "try dragging 'sleep optimization' up in personality_settings.exe instead.",
            ]);
            valid();
            return;
        }
        if (c === "resume") {
            out(["opening resume_final_final_v7.pdf ...", "tip: the Download PDF button hands you the real file, no printer involved."]);
            open("resume_pdf");
            valid();
            return;
        }
        if (c === "exit") {
            out(["shutting down ... just kidding. closing terminal."]);
            valid();
            window.setTimeout(() => close("terminal"), 500);
            return;
        }
        if (c === "clear") {
            setLines([{ id: ++idRef.current, text: "scrollback cleared. the tabs remain.", tone: "ok" }]);
            return;
        }
        if (c === "date") {
            out([new Date().toString()]);
            valid();
            return;
        }
        if (c.startsWith("echo ")) {
            out([cmd.slice(5)]);
            valid();
            return;
        }
        if (c.startsWith("open ")) {
            const name = c.slice(5).replace(/\/$/, "").trim();
            const id = APP_ALIASES[name];
            if (id) {
                out([`opening ${name} ...`]);
                open(id);
                valid();
                return;
            }
            out([`no app called '${name}'. try: open experiments`]);
            return;
        }
        out([`command not found: ${cmd}`, "type 'help'. don't worry — nothing here executes anything real."]);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            run(value);
            setValue("");
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            if (!history.length) return;
            const i = Math.min(hIdx.current + 1, history.length - 1);
            hIdx.current = i;
            setValue(history[i]);
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            const i = hIdx.current - 1;
            hIdx.current = i;
            setValue(i >= 0 ? history[i] : "");
        }
    };

    return (
        <div data-testid="terminal-app">
            <div ref={scrollRef} className="term os-scroll max-h-[280px] overflow-y-auto" aria-live="polite" data-testid="terminal-output">
                {lines.map((l) => (
                    <p key={l.id} className={l.tone === "in" ? "term-in" : ""}>
                        {l.text}
                    </p>
                ))}
            </div>
            <div className="term mt-2 flex items-center gap-2">
                <span className="flex-none">krithi@krithi-os:~$</span>
                <input
                    data-testid="terminal-input"
                    aria-label="Terminal input"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={onKeyDown}
                    autoComplete="off"
                    spellCheck={false}
                />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
                {QUICK.map((q) => (
                    <button key={q} data-testid={`terminal-chip-${q.replace(/ /g, "-")}`} className="chip hover:bg-sandsoft" onClick={() => run(q)}>
                        {q}
                    </button>
                ))}
            </div>
        </div>
    );
}
